import {type FC, useEffect, useRef} from 'react';
import {useTranslation} from 'react-i18next';
import {href, Link} from 'react-router';

import {
  type ErrorKind,
  resolveDevelopmentErrorDetails,
  resolveErrorKind,
} from '../../../utils/resolveErrorKind';
import {errorPageCriticalStyles} from './errorPageCriticalStyles';

type ErrorPageProps = {
  error: unknown;
};

const errorKindTranslationKeys: Record<
  ErrorKind,
  'generic' | 'network' | 'notFound' | 'server'
> = {
  generic: 'generic',
  network: 'network',
  notFound: 'notFound',
  server: 'server',
};

export const ErrorPage: FC<ErrorPageProps> = ({error}) => {
  const {t} = useTranslation();
  const mainReference = useRef<HTMLElement>(null);
  const errorKind = resolveErrorKind(error);
  const errorKindKey = errorKindTranslationKeys[errorKind];
  const developmentErrorDetails = resolveDevelopmentErrorDetails(error);

  useEffect(() => {
    mainReference.current?.focus();
  }, []);

  return (
    <>
      <style dangerouslySetInnerHTML={{__html: errorPageCriticalStyles}} />
      <main
        aria-labelledby="error-page-title"
        className="error-page"
        ref={mainReference}
        tabIndex={-1}
      >
        <div className="error-page__content">
          <h1 className="error-page__title" id="error-page-title">
            {t('errorPage.title')}
          </h1>

          <h2 className="error-page__context-heading">
            {t(`errorPage.${errorKindKey}.heading`)}
          </h2>

          <p className="error-page__description">
            {t(`errorPage.${errorKindKey}.description`)}
          </p>

          <p className="error-page__guidance">
            {t(`errorPage.${errorKindKey}.guidance`)}
          </p>

          <div className="error-page__actions">
            <Link
              aria-label={t('errorPage.backToHomeAriaLabel')}
              className="error-page__home-link"
              replace
              to={href('/')}
            >
              {t('errorPage.backToHome')}
            </Link>
          </div>

          {developmentErrorDetails ? (
            <details className="error-page__dev-details">
              <summary className="error-page__dev-details-summary">
                {t('errorPage.devDetailsLabel')}
              </summary>
              <pre className="error-page__dev-details-content">
                <code>{developmentErrorDetails}</code>
              </pre>
            </details>
          ) : null}
        </div>
      </main>
    </>
  );
};
